import { CommonModule } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';

import { NgxsActionName, NgxsFormPath, SelectItems } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { WebApiResponseBase } from '@app/shared/classes/general';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { KpiConfiguration } from '@app/shared/classes/kpi-weight-management';
import { OnlyNumberDirective } from '@app/shared/directive/only-number.directive';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';

@Component({
  selector: 'app-kpi-configuration-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SpinnerButtonComponent, TranslateModule, NgxsFormPluginModule, ReactiveFormsModule, MatDialogModule, AngularMaterialModule, OnlyNumberDirective],
  templateUrl: './kpi-configuration-edit-modal-dialog.component.html',
  styleUrl: './kpi-configuration-edit-modal-dialog.component.css'
})
export class KpiConfigurationEditModalDialogComponent extends EditModalDialogBasedComponent<KpiConfigurationEditModalDialogComponent, KpiConfiguration> {
  complianceSelectItems = SelectItems.complianceSelectItems;
  formPath = NgxsFormPath.KPI_CONFIGURATION_EDIT_FORM;

  @Select(AppDataState.taskExecuting(NgxsActionName.UPDATE_KPI_CONFIGURATION)) updating$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_KPI_CONFIGURATION)) response$: Observable<WebApiResponseBase>;

  constructor(public dialogRef: MatDialogRef<KpiConfigurationEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<KpiConfiguration>) {
    super();
    this.dataInit(dialogRef, data);
    this.initForm(data);
  }

  initForm = (data: InjectDialogData<KpiConfiguration>) => {
    var isPositive = SiteConfigHelper.GetKpiPointPositiveValueIdList().includes(data.data.kpi.kpiId);
    this.editForm = this.formBuilder.group({
      kpiConfiguration_Id: data.data.kpiConfiguration_Id,
      weight: [data.data.weight, [forbiddenNameValidator(isPositive), Validators.required, Validators.pattern(/^-?[0-9]\d*$/)]],
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateKpiConfiguration), withLatestFrom(this.response$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));
  }


  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateKpiConfiguration());
  }
}

function forbiddenNameValidator(needPositive: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (needPositive) {
      const forbidden = control.value < 0;
      return forbidden ? { positive: true } : null;
    }
    else {
      const forbidden = control.value > 0 || control.value < -100;
      return forbidden ? { negative: true } : null;
    }
  };
}
